import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import PremiumGate from "./components/PremiumGate";
import { Toaster } from "sonner";
import { useState, useEffect, lazy, Suspense } from "react";
import axios from "axios";
import { Layout } from "./components/Layout";
import PixelRouteTracker from "./components/PixelRouteTracker";

// Eager-import the Stripe loader so stripe.js (~600KB) starts downloading
// the moment the app boots, NOT when the user lazy-navigates to /checkout.
// Without this, the merchant pays the full Stripe.js cold-cache cost on
// the slowest possible page (the one that's gating their card entry).
import "./lib/stripe";

// Pre-warm the Meta Pixel chunk too — it's dynamic-imported by Checkout
// post-success to fire StartTrial/Subscribe events. On a cold cache that
// chunk load was adding ~100-500ms BETWEEN backend success and the user
// seeing the dashboard. Importing it here at app boot ensures the chunk
// is resident in the browser cache by the time any conversion fires.
import("./lib/metaPixel").catch(() => {});

// Code-split every route so the initial bundle ships only the shell + the page
// the user is actually on. Cuts first-paint JS from ~1.2MB down to ~400KB and
// makes subsequent tab navigations feel instant once the chunk is cached.
// Auth screens (Login/Signup/Forgot/Reset) stay eager because they're on the
// hot path for unauthenticated visitors — lazy-loading them would add a
// network round-trip to the login screen for every cold visit.
const Dashboard            = lazy(() => import("./pages/Dashboard"));
const Orders               = lazy(() => import("./pages/Orders"));
const CustomerInfluencers  = lazy(() => import("./pages/CustomerInfluencers"));
const PotentialInfluencers = lazy(() => import("./pages/PotentialInfluencers"));
const Notifications        = lazy(() => import("./pages/Notifications"));
const Settings             = lazy(() => import("./pages/Settings"));
const SocialFollowers      = lazy(() => import("./pages/SocialFollowers"));
const Watchlist            = lazy(() => import("./pages/Watchlist"));
const AdminPanel           = lazy(() => import("./pages/AdminPanel"));
const Keywords            = lazy(() => import("./pages/Keywords"));
const NameLookup          = lazy(() => import("./pages/NameLookup"));
const WooCommerceConnect   = lazy(() => import("./pages/WooCommerceConnect"));
const WooCommerceConnectV2 = lazy(() => import("./pages/WooCommerceConnectV2"));
const WooCommerceReturn    = lazy(() => import("./pages/WooCommerceReturn"));
const ShopifyConnect       = lazy(() => import("./pages/ShopifyConnect"));
const Pricing              = lazy(() => import("./pages/Pricing"));
const TrialBlocked         = lazy(() => import("./pages/TrialBlocked"));
const TrialBlockedVerifier = lazy(() => import("./pages/TrialBlockedVerifier"));
const Checkout             = lazy(() => import("./pages/Checkout"));
const AutoLogin            = lazy(() => import("./pages/AutoLogin"));

import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CardOnboarding from "./pages/CardOnboarding";

// Tiny loader shown while a lazy route chunk downloads. Keeps the sidebar +
// header visible (they're in Layout) so the app doesn't "flash" blank.
const RouteFallback = () => (
  <div className="flex items-center justify-center py-24" data-testid="route-loading">
    <div className="w-8 h-8 bg-[#9FFF00] animate-pulse" />
  </div>
);

const withLayout = (Component, user, onLogout, extraProps = {}) => (
  <Layout user={user} onLogout={onLogout}>
    <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Component {...extraProps} />
      </Suspense>
    </ErrorBoundary>
  </Layout>
);

// Wraps a route in both the layout AND a premium gate that blurs the page
// content + shows an upgrade CTA for trial users. Paid users see the page
// unchanged. Feature-specific copy keeps the pitch tight rather than
// generic. See /app/frontend/src/components/PremiumGate.jsx.
// SPEC §7 — no feature gating by tier. Every plan gets every feature.
// `withPremiumGate` used to render a PremiumGate wall for trial users; we
// keep the helper so call sites don't have to be refactored, but it now
// transparently delegates to withLayout and ignores featureName/copy.
const withPremiumGate = (Component, user, onLogout, _featureName, _featureCopy, extraProps = {}) => (
  <Layout user={user} onLogout={onLogout}>
    <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Component {...extraProps} />
      </Suspense>
    </ErrorBoundary>
  </Layout>
);

const API_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authView, setAuthView] = useState("login");

  const urlParams = new URLSearchParams(window.location.search);
  const resetToken = urlParams.get("token");
  const isConnectPage = window.location.pathname === "/connect";
  const isAutoLoginPage = window.location.pathname === "/auto-login";

  // The WordPress plugin's "Open Dashboard" button appends `?from=plugin`.
  // Strip the param AND force the URL back to "/" before BrowserRouter
  // reads it — this guarantees the merchant lands on the Dashboard
  // regardless of any stale path / browser autocomplete / session-restored
  // URL (e.g. /settings) they might've had open. ALSO bust the cached
  // `id_user` so the dashboard sees the fresh `store_id` set during the
  // /connect handshake — without this, returning users from the plugin
  // sometimes saw the onboarding screen because their localStorage
  // `id_user` was still holding pre-connect data even though
  // `/api/auth/me` would have returned the new store_id.
  if (urlParams.get("from") === "plugin" && !isConnectPage) {
    try {
      window.history.replaceState({}, "", "/");
      localStorage.removeItem("id_user");
    } catch (e) {}
  }

  useEffect(() => {
    if (resetToken && !isConnectPage) {
      setAuthView("reset");
      setAuthChecked(true);
      setUser(false);
      return;
    }
    const token = localStorage.getItem("id_token");
    if (!token) {
      setUser(false);
      setAuthChecked(true);
      return;
    }
    fetch(`${API_URL}/api/auth/verify`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error("Invalid");
      })
      .then((data) => {
        const fresh = data.user || true;
        // Keep localStorage in sync with the server so downstream hooks
        // (useNeedsCard, plan-gate checks) don't read a stale `needs_card`
        // or plan value after the user upgrades via Stripe.
        if (data.user) {
          try { localStorage.setItem("id_user", JSON.stringify(data.user)); } catch {}
        }
        setUser(fresh);
        setAuthChecked(true);
      })
      .catch(() => {
        // PostHog session recording on production monkey-patches `fetch`
        // and consumes the response body before we can read it, throwing
        // intermittently on this verify call. We retry via axios + XHR
        // adapter (immune to the fetch hijack) before logging the user
        // out — without this, returning users were force-logged-out at
        // random page loads.
        axios
          .get(`${API_URL}/api/auth/verify`, {
            adapter: "xhr",
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((res) => {
            const data = res.data || {};
            const fresh = data.user || true;
            if (data.user) {
              try { localStorage.setItem("id_user", JSON.stringify(data.user)); } catch {}
            }
            setUser(fresh);
            setAuthChecked(true);
          })
          .catch(() => {
            localStorage.removeItem("id_token");
            localStorage.removeItem("id_user");
            setUser(false);
            setAuthChecked(true);
          });
      });
  }, [resetToken, isConnectPage]);

  // PERF: lets non-App.js code (e.g. Checkout.jsx after a successful
  // /billing/finalize-trial) refresh App.js's `user` state in-place
  // without doing a full page reload. Avoids a ~1-2s cold app boot
  // every time the merchant completes a card-on-file step.
  useEffect(() => {
    const handler = () => {
      const token = localStorage.getItem("id_token");
      if (!token) return;
      axios
        .get(`${API_URL}/api/auth/verify`, {
          adapter: "xhr",
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const fresh = res?.data?.user;
          if (fresh) {
            try { localStorage.setItem("id_user", JSON.stringify(fresh)); } catch {}
            setUser(fresh);
          }
        })
        .catch(() => { /* leave existing state */ });
    };
    window.addEventListener("id-user-refresh", handler);
    return () => window.removeEventListener("id-user-refresh", handler);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("id_token");
    localStorage.removeItem("id_user");
    setUser(false);
    setAuthView("login");
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-12 h-12 bg-[#9FFF00] flex items-center justify-center animate-pulse">
          <span className="text-black font-black text-base">ID</span>
        </div>
      </div>
    );
  }

  // Auto-login handoff — admin-issued one-click sign-in URL.
  // MUST short-circuit BEFORE the `if (!user)` auth gate, otherwise the
  // login screen would render and immediately replace our token-handling
  // logic. Renders without Layout chrome; AutoLogin itself stores the
  // token and `window.location.replace`s onward.
  if (isAutoLoginPage) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AutoLogin />
      </Suspense>
    );
  }

  // WooCommerce connect page — needs auth but separate UI
  if (isConnectPage) {
    if (!user) {
      return (
        <>
          <Toaster position="top-right" richColors />
          <Login
            onAuthenticated={(u) => setUser(u)}
            onSwitchToSignup={() => setAuthView("signup")}
            onSwitchToForgot={() => setAuthView("forgot")}
          />
        </>
      );
    }
    return (
      <>
        <Toaster position="top-right" richColors />
        <Suspense fallback={<RouteFallback />}>
          <WooCommerceConnect />
        </Suspense>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Toaster position="top-right" richColors />
        {authView === "login" && (
          <Login
            onAuthenticated={(u) => setUser(u)}
            onSwitchToSignup={() => setAuthView("signup")}
            onSwitchToForgot={() => setAuthView("forgot")}
          />
        )}
        {authView === "signup" && (
          <Signup
            onAuthenticated={(u) => setUser(u)}
            onSwitchToLogin={() => setAuthView("login")}
          />
        )}
        {authView === "forgot" && (
          <ForgotPassword onSwitchToLogin={() => setAuthView("login")} />
        )}
        {authView === "reset" && (
          <ResetPassword token={resetToken} onSwitchToLogin={() => { setAuthView("login"); window.history.replaceState({}, "", "/"); }} />
        )}
      </>
    );
  }

  // Trial-abuse gate (Layer 1) moved from a full-screen block to an inline
  // lock on the first-influencer detection screen. `needs_card=true` users
  // now drop straight into the app after signup; the detection celebration
  // blurs the avatar and gates the "See full profile" button until they
  // attach a card. The `<CardOnboarding>` verifier below still handles the
  // post-Stripe redirect when it carries `?status=success&session_id=…`.
  if (user && user.needs_card) {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get("status");
    const sessionId = urlParams.get("session_id");
    if (status === "success" && sessionId) {
      return (
        <>
          <Toaster position="top-right" richColors />
          <ErrorBoundary>
            <CardOnboarding
              user={user}
              onComplete={(updated) => setUser(updated)}
              onLogout={handleLogout}
            />
          </ErrorBoundary>
        </>
      );
    }
  }

  // Trial-reuse block — same pattern as needs_card. When a trial-blocked
  // user pays and returns with `?status=success&session_id=…`, the Stripe
  // webhook is async so the React state may still have `trial_blocked:true`
  // (stale from localStorage at login time). We intercept the redirect,
  // poll `/billing/status/{id}` (which triggers the webhook's server-side
  // upgrade path) + `/auth/me` until the backend clears the block, then
  // refresh React state and let the user into the app normally.
  if (user && user.trial_blocked) {
    const urlParams = new URLSearchParams(window.location.search);
    // Both paid endpoints signal success via different query params:
    //   /billing/start-trial → ?status=success&session_id=...
    //   /billing/upgrade     → ?payment=success&session_id=...
    // Accept either so trial-blocked users returning from the Pricing-page
    // upgrade flow also hit the verifier.
    const paymentSucceeded =
      urlParams.get("status") === "success" || urlParams.get("payment") === "success";
    const sessionId = urlParams.get("session_id");
    if (paymentSucceeded && sessionId) {
      return (
        <>
          <Toaster position="top-right" richColors />
          <ErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
              <TrialBlockedVerifier
                sessionId={sessionId}
                onComplete={(updated) => setUser(updated)}
                onFail={() => { /* fall through to block screen on timeout */ }}
              />
            </Suspense>
          </ErrorBoundary>
        </>
      );
    }
  }

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <ErrorBoundary>
        <BrowserRouter>
          <PixelRouteTracker />
          <Routes>
            {/* Trial-blocked users can ONLY visit the block screen + the
                pricing flow. Every other route redirects back. This is a
                hard guard, not a gate — the app is unusable until they
                pay (by design; the trial was already consumed). */}
            {user?.trial_blocked ? (
              <>
                <Route
                  path="/trial-blocked"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <TrialBlocked user={user} onLogout={handleLogout} />
                    </Suspense>
                  }
                />
                <Route path="/pricing" element={withLayout(Pricing, user, handleLogout)} />
                <Route
                  path="/checkout/:tier"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<RouteFallback />}>
                        <Checkout />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/checkout"
                  element={<Navigate to="/checkout/growth" replace />}
                />
                <Route path="*" element={<Navigate to="/trial-blocked" replace />} />
              </>
            ) : (
              <>
                <Route path="/"                      element={withLayout(Dashboard, user, handleLogout)} />
                <Route
                  path="/checkout/:tier"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<RouteFallback />}>
                        <Checkout />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/checkout"
                  element={<Navigate to="/checkout/growth" replace />}
                />
                <Route path="/orders"                element={withLayout(Orders, user, handleLogout)} />
                <Route path="/influencers"           element={withLayout(CustomerInfluencers, user, handleLogout)} />
                <Route path="/potential-influencers" element={withPremiumGate(PotentialInfluencers, user, handleLogout, "Subscriber Influencers", "Automatically surface influencers from your email subscriber list — people who haven't ordered yet but already follow you. Each subscriber is cross-referenced against Instagram, TikTok, and YouTube to spot the high-reach ones buried in your list.")} />
                <Route path="/social-followers"      element={withPremiumGate(SocialFollowers, user, handleLogout, "Social Media Followers", "Upload your own Instagram or TikTok follower list and instantly see which of those followers are also influencers — sorted by reach, verified status, and engagement. Turns your existing audience into a discovery engine.")} />
                <Route path="/watchlist"             element={withPremiumGate(Watchlist, user, handleLogout, "Watchlist", "Track specific influencers 24/7. Get a notification the moment any of them orders, posts about your brand, or crosses a follower milestone. Your personal intel feed on the creators that matter most.")} />
                <Route path="/keywords"              element={withPremiumGate(Keywords, user, handleLogout, "Keyword Auto-Tagging", "Create custom keywords like \"PGA\", \"Fitness\", or \"Mom Creator\" and we'll auto-tag every matching influencer — scanning their bios, emails, handles, and AI analysis. Filter your dashboard by tag and spot niche creators you'd otherwise miss.")} />
                <Route path="/name-lookup"           element={withLayout(NameLookup, user, handleLogout)} />
                <Route path="/pricing"               element={withLayout(Pricing, user, handleLogout)} />
                <Route path="/notifications"         element={withLayout(Notifications, user, handleLogout)} />
                <Route path="/settings"              element={withLayout(Settings, user, handleLogout, { user, onLogout: handleLogout })} />
                {/* Native WooCommerce REST-API connect flow ("Option C")
                    — coexists with the WP-plugin token bridge at /connect.
                    Both routes intentionally live OUTSIDE withLayout because
                    the merchant comes here mid-redirect and the layout chrome
                    (sidebar / header) would just be visual noise during the
                    "Finishing connection…" polling state on mobile. */}
                <Route
                  path="/woocommerce/connect"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <WooCommerceConnectV2 />
                    </Suspense>
                  }
                />
                <Route
                  path="/woocommerce/return"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <WooCommerceReturn />
                    </Suspense>
                  }
                />
                {/* Shopify Custom App "live connect" flow — sits outside
                    withLayout for the same reason as WooCommerce: it's
                    a focused step-by-step page where the sidebar would
                    just compete for attention. */}
                <Route
                  path="/shopify/connect"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <ShopifyConnect />
                    </Suspense>
                  }
                />
                {user?.role === "admin" && (
                  <Route path="/admin"               element={withLayout(AdminPanel, user, handleLogout)} />
                )}
                {/* Catch-all: unknown path (or /admin for non-admins) → send to dashboard */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
